/**
 * Voix d'appel de l'écran — file d'attente speechSynthesis.
 *
 * Contrat 2026-10-23 : les annonces sont des lignes { langue, texte, priorite }
 * (SignalR : camelCase ; réponse HTTP : PascalCase — les deux sont acceptées).
 *
 * - Lues par priorité croissante, l'une après l'autre (1 à 3 langues).
 * - Voix choisie par les 2 premières lettres de la langue (« fr », « en », « es »…).
 *   Aucune voix installée pour la langue -> annonce SAUTÉE (jamais lue avec la
 *   voix d'une autre langue).
 * - rate = VitesseAppel du service (1 par défaut).
 * - Jamais de coupure : les annonces s'enchaînent dans la file.
 * - Attend « voiceschanged » au démarrage (liste vide sous Linux).
 */

interface Ligne {
    langue: string;
    texte: string;
    priorite: number;
}

/** Normalise une ligne (camelCase ou PascalCase). */
function normLigne(a: any): Ligne | null {
    const langue = a?.langue ?? a?.Langue ?? ''
    const texte = a?.texte ?? a?.Texte ?? ''
    if (!langue || !texte) return null
    return { langue: String(langue), texte: String(texte), priorite: Number(a?.priorite ?? a?.Priorite ?? 1) || 1 }
}

const file: { lignes: Ligne[]; rate: number }[] = [];
let enCours = false;

/** Attend que la liste des voix soit disponible (Linux : vide au démarrage). */
function attendreVoix(): Promise<SpeechSynthesisVoice[]> {
    const voix = window.speechSynthesis?.getVoices() || []
    if (voix.length > 0) return Promise.resolve(voix)
    return new Promise((resolve) => {
        const done = () => {
            window.speechSynthesis.removeEventListener('voiceschanged', done)
            resolve(window.speechSynthesis.getVoices() || [])
        }
        window.speechSynthesis.addEventListener('voiceschanged', done)
        // Filet : si le navigateur ne déclenche jamais voiceschanged
        setTimeout(() => resolve(window.speechSynthesis.getVoices() || []), 3000)
    })
}

/** Voix dont la langue commence par les 2 lettres de la langue demandée. */
function choisirVoix(voix: SpeechSynthesisVoice[], langue: string): SpeechSynthesisVoice | undefined {
    const prefixe = String(langue || '').slice(0, 2).toLowerCase()
    if (!prefixe) return undefined
    return voix.find(v => v.lang?.toLowerCase().startsWith(prefixe))
}

/** Prononce une phrase et attend sa fin (garde de 20 s : voix bloquée). */
function parler(ligne: Ligne, voix: SpeechSynthesisVoice | undefined, rate: number): Promise<void> {
    return new Promise((resolve) => {
        let fini = false
        const fin = () => { if (!fini) { fini = true; clearTimeout(garde); resolve() } }
        const garde = setTimeout(fin, 20000)
        try {
            const u = new SpeechSynthesisUtterance(ligne.texte)
            if (voix) u.voice = voix
            u.lang = voix?.lang || ligne.langue
            u.rate = rate || 1
            u.onend = fin
            u.onerror = fin
            window.speechSynthesis.speak(u)
        } catch { fin() }
    })
}

/* ---------- Appel d'écran (AppelEcran) : un seul à la fois, annulable ---------- */

let generation = 0

/** Coupe immédiatement la voix en cours (nouvel appel reçu). */
export function couperVoix() {
    generation++
    try { window.speechSynthesis?.cancel() } catch { /* ignore */ }
}

/**
 * Lit un appel (lignes triées par priorité), phrase après phrase.
 * `onLigne(i, texte)` est appelé au début de chaque phrase lue.
 * Résout `true` si la dernière phrase est finie, `false` si l'appel a été
 * remplacé entre-temps (couperVoix).
 */
export async function lireAppel(annonces: any[], rate = 1, onLigne?: (i: number, texte: string) => void): Promise<boolean> {
    couperVoix()
    const gen = generation
    if (!window.speechSynthesis) return true
    const lignes = (annonces || [])
        .map(normLigne)
        .filter((a): a is Ligne => !!a)
        .sort((a, b) => a.priorite - b.priorite)
    const voix = await attendreVoix()
    for (let i = 0; i < lignes.length; i++) {
        if (gen !== generation) return false
        const v = choisirVoix(voix, lignes[i].langue)
        if (!v) continue                       // langue non installée : sautée
        onLigne?.(i, lignes[i].texte)
        await parler(lignes[i], v, rate || 1)
    }
    return gen === generation
}

async function jouerSuivante(voix: SpeechSynthesisVoice[]) {
    const annonce = file.shift()
    if (!annonce) { enCours = false; return }
    enCours = true
    for (const ligne of annonce.lignes) {
        const v = choisirVoix(voix, ligne.langue)
        if (!v) continue                       // langue non installée : sautée
        await parler(ligne, v, annonce.rate)
    }
    jouerSuivante(voix)
}

/**
 * Empile une annonce multi-langues (triée par priorité croissante).
 * Jamais interrompue par l'annonce suivante.
 */
export function annoncer(annonces: any[], rate = 1) {
    if (!window.speechSynthesis) return
    const lignes = (annonces || [])
        .map(normLigne)
        .filter((a): a is Ligne => !!a)
        .sort((a, b) => a.priorite - b.priorite)
    if (lignes.length === 0) return
    file.push({ lignes, rate: rate || 1 })
    if (enCours) return
    attendreVoix()
        .then(jouerSuivante)
        .catch(() => { enCours = false })
}
